import {
	Component,
	ElementRef,
	Input,
	OnDestroy,
	OnInit,
	TemplateRef,
	ViewChild,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DashboardService } from '../../services/dashboard.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PrintService } from '../../services/print.service';
import { buildDashboardThemeVars } from '../dashboard-widget-theme';
import { SalesTargetEditorComponent } from '../sales-target-editor/sales-target-editor.component';

@Component({
	selector: 'app-units-this-last',
	templateUrl: './units-this-last.component.html',
	styleUrls: ['./units-this-last.component.scss'],
})
export class UnitsThisLastComponent implements OnInit, OnDestroy {
	@Input() config: any = false;
	@Input() userdata: any = false;
	@Input() dummyData = false;
	@Input() dummyThisoverlast: any = false;

	@ViewChild('print_table') printtable: ElementRef;
	@ViewChild('monthView') monthViewRef: TemplateRef<any>;

	thisoverlast: any = false;
	yearCount = 3;
	loading = true;
	datepicked: Date | false = false;

	private dataSub: Subscription | null = null;
	private readonly destroy$ = new Subject<void>();

	get themeVars(): Record<string, string> {
		return buildDashboardThemeVars('tol', 'success');
	}

	get unitsPctClass(): string {
		if (!this.thisoverlast) {
			return '';
		}
		return this.thisoverlast.percent_updown_units < 0 ? 'text-danger' : 'text-success';
	}

	get salesPctClass(): string {
		if (!this.thisoverlast) {
			return '';
		}
		return this.thisoverlast.percent_updown_dollars < 0 ? 'text-danger' : 'text-success';
	}

	constructor(
		private dashboardService: DashboardService,
		private globalSearchService: GlobalSearchService,
		private printService: PrintService,
		private modalService: NgbModal,
	) {}

	ngOnInit(): void {
		this.loadData();
	}

	ngOnDestroy(): void {
		this.dataSub?.unsubscribe();
		this.destroy$.next();
		this.destroy$.complete();
	}

	loadData(): void {
		if (this.dummyData) {
			this.thisoverlast = this.dummyThisoverlast || false;
			this.loading = !this.thisoverlast;
			return;
		}

		this.loading = true;
		this.dataSub?.unsubscribe();

		const filters = this.userdata?.user?.issalesman
			? { salesman: this.userdata.user.salesman, yearCount: this.yearCount }
			: { yearCount: this.yearCount };

		const request$ = this.userdata?.user?.issalesman
			? this.dashboardService.getThisYearOverLastSalesman(filters)
			: this.dashboardService.getThisYearOverLast(filters);

		this.dataSub = request$.pipe(takeUntil(this.destroy$)).subscribe({
			next: (results: any) => {
				this.thisoverlast = results;
				this.thisoverlast.targets = this.dashboardService.mergeLocalTargets(results.targets, this.userdata);
				this.thisoverlast.mtd_target = this.dashboardService.currentMonthTarget(this.thisoverlast.targets, results.mtd_target);
				this._monthTrendsCache.clear();
				this.loading = false;
			},
			error: () => {
				this.thisoverlast = null;
				this.loading = false;
			},
		});
	}

	getSaleTotalYear(year: any): number {
		if (!year?.sales?.length) {
			return 0;
		}
		return year.sales.reduce((accumulator: number, items: any) => accumulator + (parseFloat(items.value) || 0), 0);
	}

	getQtyTotalYear(year: any): number {
		if (!year?.qty?.length) {
			return 0;
		}
		return year.qty.reduce((accumulator: number, items: any) => accumulator + (parseFloat(items.value) || 0), 0);
	}

	private _monthLabelsCache: { src: any; value: string[] } = { src: null, value: [] };
	/** Month labels for the combined-table header, taken from the newest year. */
	get monthLabels(): string[] {
		const first = this.thisoverlast?.years?.[0] || null;
		if (this._monthLabelsCache.src !== first) {
			this._monthLabelsCache = { src: first, value: (first?.sales || []).map((m: any) => m.label) };
		}
		return this._monthLabelsCache.value;
	}

	/** Compact Y/M growth chips for a month cell. Returns null for the oldest
	 *  year (nothing to compare against) and for empty months with no movement,
	 *  so future months don't render meaningless 0% noise. */
	private _monthTrendsCache = new Map<string, any>();
	monthTrends(monthLabel: string, year: any, yearIndex: number): Array<{ key: string; pct: number; dir: 'up' | 'down' | 'flat' }> | null {
		const cacheKey = `${yearIndex}:${monthLabel}`;
		if (this._monthTrendsCache.has(cacheKey)) {
			return this._monthTrendsCache.get(cacheKey);
		}
		const result = this._computeMonthTrends(monthLabel, year, yearIndex);
		this._monthTrendsCache.set(cacheKey, result);
		return result;
	}

	private _computeMonthTrends(monthLabel: string, year: any, yearIndex: number): Array<{ key: string; pct: number; dir: 'up' | 'down' | 'flat' }> | null {
		if (!this.thisoverlast?.years || yearIndex === this.thisoverlast.years.length - 1) {
			return null;
		}
		const obj = (year.qty || []).find((m: any) => m.label === monthLabel);
		if (!obj) {
			return null;
		}
		const yearPct = obj.monthgrowth === -1 ? 0 : (parseFloat(obj.monthgrowth) || 0);
		const monthPct = obj.monthpreviousgrowth === -1 ? 0 : (parseFloat(obj.monthpreviousgrowth) || 0);
		const hasQty = (parseFloat(obj.value) || 0) !== 0;
		if (!hasQty && yearPct === 0 && monthPct === 0) {
			return null;
		}
		const dir = (p: number): 'up' | 'down' | 'flat' => (p > 0 ? 'up' : (p < 0 ? 'down' : 'flat'));
		return [
			{ key: 'Y', pct: yearPct, dir: dir(yearPct) },
			{ key: 'M', pct: monthPct, dir: dir(monthPct) },
		];
	}

	// ─── Sales targets ────────────────────────────────────────────────────

	/** Admins (non-salesmen) can maintain targets from the widget header. */
	get canEditTargets(): boolean {
		return !this.userdata?.user?.issalesman;
	}

	get mtdTarget(): number {
		return parseFloat(this.thisoverlast?.mtd_target) || 0;
	}

	get mtdTargetAttainment(): number {
		const target = this.mtdTarget;
		return target > 0 ? (parseFloat(this.thisoverlast?.mtd_dollars) || 0) / target : 0;
	}

	targetFor(year: any, monthnumber: any): number {
		const period = `${year}-${String(monthnumber).padStart(2, '0')}`;
		return parseFloat(this.thisoverlast?.targets?.[period]) || 0;
	}

	yearHasTargets(year: any): boolean {
		const code = year?.qty?.[0]?.year || year?.sales?.[0]?.year;
		if (!code || !this.thisoverlast?.targets) {
			return false;
		}
		return Object.keys(this.thisoverlast.targets).some(p => p.startsWith(code + '-') && parseFloat(this.thisoverlast.targets[p]) > 0);
	}

	yearTargetTotal(year: any): number {
		const code = year?.qty?.[0]?.year || year?.sales?.[0]?.year;
		if (!code || !this.thisoverlast?.targets) {
			return 0;
		}
		return Object.keys(this.thisoverlast.targets)
			.filter(p => p.startsWith(code + '-'))
			.reduce((sum, p) => sum + (parseFloat(this.thisoverlast.targets[p]) || 0), 0);
	}

	/** Attainment ratio for a month cell; null when no target is set. */
	attainmentFor(month: any): number | null {
		const target = this.targetFor(month.year, month.monthnumber);
		if (!target) {
			return null;
		}
		return (parseFloat(month.value) || 0) / target;
	}

	openTargetEditor(): void {
		this.modalService.open(SalesTargetEditorComponent, { size: 'lg', animation: false })
			.result.then((result) => {
				if (result === 'saved') {
					this.loadData();
				}
			}, () => {});
	}

	viewHistory(month: any, year: any): void {
		let monthStr = String(month);
		if (parseInt(monthStr, 10) < 10) {
			monthStr = '0' + monthStr;
		}

		this.datepicked = new Date(`${year}-${monthStr}-15`);
		this.modalService.open(this.monthViewRef, {
			ariaLabelledBy: 'modal-title',
			size: 'xl',
			animation: false,
		}).result.then(() => {}, () => {});
	}

	exportXls(): void {
		const now = new Date();
		const element = this.printtable?.nativeElement?.innerHTML ?? '';
		const data = {
			content: this.globalSearchService.base64encode(element),
			filename: `ThisLast ${now.getMonth() + 1}${now.getDate()}${now.getFullYear()}`,
		};

		this.printService.xls(data).pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
			this.globalSearchService.downloadXls(result.content, data.filename);
		});
	}

	exportPdf(): void {
		const now = new Date();
		const element = this.printtable?.nativeElement?.innerHTML ?? '';
		const data = {
			content: this.globalSearchService.base64encode(element),
			filename: `ThisLast ${now.getMonth() + 1}${now.getDate()}${now.getFullYear()}`,
			title: `This/Last - Created @ ${now.toLocaleString()}`,
			subtitle: '',
		};

		this.printService.pdf(data).pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
			this.globalSearchService.downloadPdf(result.content, data.filename);
		});
	}
}
